import { useCallback, useEffect, useRef, useState } from "react";
import i18n from "@lib/i18n";
import { METADATA_FIELDS_TYPE, REFERENCE_FIELDS_TYPE } from "@constants";
import { useSelector } from "react-redux";
import { getAssetsAPI } from "@modules/apis/assets";
import { getCollectionsAPI } from "@modules/apis/collections";
import { getVideosAPI } from "@modules/videos";
import MusicApi from "@modules/apis/music";
import PhotoApi from "@modules/apis/photo";
import { getPeopleListAPI } from "@modules/people";
import { getCustomContentsAPI } from "@modules/apis/custom-contents";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import { useAutocompleteTable } from "@hooks/useAutocompleteTable";
import { getCustomContentLinksAPI } from "@modules/apis/custom-content";
import { RANGE_DATE_PATTERN } from "@modules/content-schema";

// createDefaultValueSchema가 거는 필수 검사의 테스트 이름. 타입별 브랜치가 모두 이 이름을 쓰기 때문에
// 검증 결과에서 "필수 미입력"만 골라낼 수 있다(hasRequiredError). yup의 string/object/array required()가
// 기본으로 쓰는 이름이라 number/array 커스텀 테스트도 여기에 맞춘다.
const REQUIRED_TEST_NAME = "required";

/**
 * validationRule 위반 중 "필수 미입력"만 판별한다. min/max/pattern·허용 옵션 등 다른 규칙 위반은 무시한다.
 *
 * isValidSync는 boolean만 반환해 위반 종류를 구분할 수 없으므로 validateSync로 받아
 * 테스트 이름(ValidationError.type)으로 거른다.
 *
 * @param {object|null} validationRule createDefaultValueSchema 산출 스키마(없으면 검사 대상 아님)
 * @param {*} value 검사할 값
 * @returns {boolean} 필수인데 값이 비어 있으면 true
 */
export const hasRequiredError = (validationRule, value) => {
    if (!validationRule) return false;
    try {
        validationRule.validateSync({ fieldValue: value }, { abortEarly: false });
        return false;
    } catch (e) {
        // ValidationError가 아닌 예외(스키마 구현 오류 등)는 미입력으로 취급하지 않는다.
        const errors = e?.inner?.length ? e.inner : [e];
        return errors.some((error) => error?.type === REQUIRED_TEST_NAME);
    }
};

const useCustomContent = () => {
    const LIMIT = 20;
    const offsetRef = useRef(0);
    const nextTokenRef = useRef(null);
    const { schemaId, contentId } = useParams();

    const { getResourceType, getTableColumns, getSearchTableColumns, getSmallTableColumns, getSubNameIds } =
        useAutocompleteTable();

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);
    const [results, setResults] = useState(null);
    const [pending, setPending] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [viewMore, setViewMore] = useState(false);

    useEffect(() => {
        setViewMore((results?.length > 0 && results?.length < totalCount) || !!nextTokenRef.current);
    }, [results, totalCount]);

    const resetParams = async () => {
        nextTokenRef.current = null;
        setTotalCount(0);
        setViewMore(false);
        setResults(null);
        offsetRef.current = 0;
    };

    const onResourceFetch = async ({ detailType, ids, searchKeyword, limit, ...rest }) => {
        if (!detailType) return;

        const type = getResourceType(detailType);
        let params = {
            stageId: stage.id,
            stageEndpoint: stage.endpoint,
            projectId: project.id,
        };
        if (ids) {
            params = { ...params, search: { ids } };
        }
        if (rest) params.search = { ...params.search, ...rest };
        params.search = { ...params.search, limit: limit ?? LIMIT };

        switch (type) {
            case "ASSET":
                params = {
                    ...params,
                    search: {
                        ...params.search,
                        generateAccessUrl: true,
                        mediaTypes: [getAssetType(detailType)],
                        offset: offsetRef.current,
                        archiveStatus: "ALL", //NOTE: 에셋 필드에 추가 후 아카이브된 에셋도 커스텀 컨텐츠에서 노출시키기 위해 추가
                        includeShareInfo: true,
                    },
                };
                if (searchKeyword) params.search = { ...params.search, name: searchKeyword, offset: 0 };
                return await getResourceAssetData(type, params);
            case "CONTENT": {
                const contentType = getContentType(detailType);
                if (contentType === "CUSTOM")
                    params = {
                        ...params,
                        search: {
                            ids,
                            contentSchemaId: schemaId,
                            nextToken: nextTokenRef.current,
                        },
                    };
                else
                    params = {
                        ...params,
                        search: {
                            ...params.search,
                            generateAccessUrl: true,
                            offset: offsetRef.current,
                        },
                    };

                if (searchKeyword) params.search = { ...params.search, name: searchKeyword, offset: 0 };
                return await getResourceAssetData(contentType, params);
            }
            case "COLLECTION":
                params = { ...params, search: { ...params.search, ids: params.search?.ids?.join(",") } };
                if (nextTokenRef.current) params.search = { ...params.search, nextToken: nextTokenRef.current };
                if (searchKeyword) params.search = { ...params.search, name: searchKeyword };
                return await getResourceAssetData(type, params);
            case "LINK":
                params = { ...params, search: { ...params.search, ids: ids?.join(",") } };
                if (nextTokenRef) params.search = { ...params.search, nextToken: nextTokenRef.current };
                return await getResourceAssetData(type, params);
        }
    };

    const getResourceAssetData = async (type, params) => {
        if (pending) return;
        setPending(true);
        try {
            const { stageId, stageEndpoint, projectId, search } = params;
            const preResults = offsetRef.current > 0 || !!nextTokenRef.current ? results : [];
            let response = null;
            let _results = null;

            if (type === "ASSET") {
                response = await getAssetsAPI(stageId, stageEndpoint, projectId, search);
                offsetRef.current = offsetRef.current + search.limit;

                setTotalCount(response.data?.totalCount);
                _results = [...preResults, ...(response.data?.assets || [])];
            } else if (type === "COLLECTION") {
                response = await getCollectionsAPI(stageEndpoint, projectId, search);
                nextTokenRef.current = response.data?.nextToken;
                _results = [...preResults, ...(response.data?.results || [])];
            } else if (type === "VIDEO") {
                response = await getVideosAPI(stageId, stageEndpoint, projectId, search);
                offsetRef.current = offsetRef.current + search.limit;
                const newResponse = response.data?.videos.map((el) => ({ ...el, contentType: type }));
                setTotalCount(response.data?.totalCount);
                _results = [...preResults, ...newResponse];
            } else if (type === "MUSIC") {
                response = await MusicApi.getMusicsAsync(stageId, stageEndpoint, projectId, search);
                const newResponse = response.data?.contents.map((el) => ({ ...el, contentType: type }));
                offsetRef.current = offsetRef.current + search.limit;
                setTotalCount(response.data?.totalCount);
                _results = [...preResults, ...newResponse];
            } else if (type === "PHOTO") {
                response = await PhotoApi.getPhotosAsync(stageId, stageEndpoint, projectId, search);
                const newResponse = response.data?.contents.map((el) => ({ ...el, contentType: type }));
                offsetRef.current = offsetRef.current + search.limit;
                setTotalCount(response.data?.totalCount);
                _results = [...preResults, ...newResponse];
            } else if (type === "PEOPLE") {
                response = await getPeopleListAPI(stageId, stageEndpoint, projectId, search);
                const newResponse = response.data?.people.map((el) => ({ ...el, contentType: type }));
                offsetRef.current = offsetRef.current + search.limit;
                setTotalCount(response.data?.totalCount);
                _results = [...preResults, ...newResponse];
            } else if (type === "CUSTOM") {
                response = await getCustomContentsAPI(stageId, stageEndpoint, projectId, search);
                const newResponse = response.data?.results.map((el) => ({ ...el, contentType: type }));
                nextTokenRef.current = response.data?.nextToken;
                _results = [...preResults, ...newResponse];
            } else if (type === "LINK") {
                response = await getCustomContentLinksAPI(stageEndpoint, projectId, search);
                const newResponse = response.data?.results.map((el) => ({ ...el, contentType: type }));
                nextTokenRef.current = response.data?.nextToken;
                _results = [...preResults, ...newResponse];
                _results.sort((a, b) =>
                    b.name.toLowerCase().localeCompare(a.name.toLowerCase(), undefined, {
                        numeric: true,
                        sensitivity: "base",
                    }),
                );
            }
            setResults(_results);
            return _results;
        } catch (e) {
            console.error(e);
            return null;
        } finally {
            setPending(false);
        }
    };

    const getResourceTitle = (isReference, detailType) => {
        const detailTypeArray = detailType.split("_");
        let title = detailTypeArray.map((el) => el.charAt(0) + el.slice(1).toLowerCase()).join(" ");

        return `${isReference ? "Reference" : "Metadata"} ${title}`;
    };

    const getOptionsBySelectType = (field, fieldType) => {
        const isSingleEnum = fieldType === METADATA_FIELDS_TYPE.SINGLE_SELECT;
        if (isSingleEnum) {
            return field?.fieldType?.dataType?.enum;
        } else {
            const properties = field?.fieldType?.dataType?.properties;
            return properties?.length > 0 ? properties[0].enum : [];
        }
    };

    const getCustomMetadataOptions = (field) => {
        const fieldType = field.fieldType.dataType.detailType;
        const isSingleEnum = fieldType === METADATA_FIELDS_TYPE.SINGLE_SELECT;
        const isMultiEnum = fieldType === METADATA_FIELDS_TYPE.MULTI_SELECT;
        if (isSingleEnum) return field?.fieldType?.dataType?.enum;
        if (isMultiEnum) {
            const properties = field?.fieldType?.dataType?.properties;
            return properties?.length > 0 ? properties[0].enum : [];
        }
        return [];
    };

    const getContentType = (detailType) => {
        if ([REFERENCE_FIELDS_TYPE.MUSIC_CONTENT, REFERENCE_FIELDS_TYPE.MUSIC_CONTENTS].includes(detailType))
            return "MUSIC";
        else if ([REFERENCE_FIELDS_TYPE.PEOPLE_CONTENT, REFERENCE_FIELDS_TYPE.PEOPLE_CONTENTS].includes(detailType))
            return "PEOPLE";
        else if ([REFERENCE_FIELDS_TYPE.PHOTO_CONTENT, REFERENCE_FIELDS_TYPE.PHOTO_CONTENTS].includes(detailType))
            return "PHOTO";
        else if ([REFERENCE_FIELDS_TYPE.VIDEO_CONTENT, REFERENCE_FIELDS_TYPE.VIDEO_CONTENTS].includes(detailType))
            return "VIDEO";
        else if ([REFERENCE_FIELDS_TYPE.CUSTOM_CONTENT, REFERENCE_FIELDS_TYPE.CUSTOM_CONTENTS].includes(detailType)) {
            return "CUSTOM";
        }
        return "";
    };

    const getAssetType = (detailType) => {
        if ([REFERENCE_FIELDS_TYPE.IMAGE_ASSET, REFERENCE_FIELDS_TYPE.IMAGE_ASSETS].includes(detailType))
            return "IMAGE";
        else if ([REFERENCE_FIELDS_TYPE.AUDIO_ASSET, REFERENCE_FIELDS_TYPE.AUDIO_ASSETS].includes(detailType))
            return "AUDIO";
        else if ([REFERENCE_FIELDS_TYPE.VIDEO_ASSET, REFERENCE_FIELDS_TYPE.VIDEO_ASSETS].includes(detailType))
            return "VIDEO";
        else if ([REFERENCE_FIELDS_TYPE.CAPTION_ASSET, REFERENCE_FIELDS_TYPE.CAPTION_ASSETS].includes(detailType))
            return "CAPTION";
        else if ([REFERENCE_FIELDS_TYPE.FILE_ASSET, REFERENCE_FIELDS_TYPE.FILE_ASSETS].includes(detailType))
            return "FILE";
        else if (detailType.indexOf("PREVIEW") >= 0) return "PREVIEW";
        return "";
    };

    const getResourceTableColumns = (detailType) => {
        const type = getResourceType(detailType);
        let subType = "";

        if (type === "ASSET") {
            subType = getAssetType(detailType);
        } else if (type === "CONTENT") {
            subType = getContentType(detailType);
        }

        return getTableColumns(type, subType);
    };

    const getResourceSearchTableColumns = (detailType) => {
        const type = getResourceType(detailType);
        let subType = "";

        if (type === "ASSET") {
            subType = getAssetType(detailType);
        } else if (type === "CONTENT") {
            subType = getContentType(detailType);
        }

        return getSearchTableColumns(type, subType);
    };

    const getResourceSmallTableColumns = (detailType) => {
        const type = getResourceType(detailType);
        let subType = "";

        if (type === "ASSET") {
            subType = getAssetType(detailType);
        } else if (type === "CONTENT") {
            subType = getContentType(detailType);
        }

        return getSmallTableColumns(type, subType);
    };

    // options: enum(SINGLE_SELECT/MULTI_SELECT) 필드의 허용 옵션 목록.
    // 넘기지 않으면 옵션 멤버십 검사를 건너뛴다.
    const createDefaultValueSchema = (type, subType, rules, addRequired, options) => {
        let schema;

        if ((type === "array" && !subType) || (type !== "array" && subType) || !type) {
            return null;
        }

        const fieldType = type === "array" ? subType : type;

        if (fieldType === "string") {
            // addRequired는 스키마 생성 시점에 확정된 값이라 when으로 지연시키지 않는다 —
            // when은 검증 시점에 base.concat(then)을 수행하고 then의 spec이 base를 덮으므로,
            // then 쪽 yup.string()(nullable 기본 false)이 .nullable(true)를 무효화해
            // null 값에서 required보다 typeError가 먼저 발생한다
            schema = yup.string().nullable(true);
            if (addRequired) {
                schema = schema.required(i18n.t(`common::msg::This field is required.`, `This field is required.`));
            }
            if (rules.min) {
                schema = schema.min(rules?.min, i18n.t(`common::msg::This field can be at least {{minLength}} characters.`, `This field can be at least {{minLength}} characters.`, { minLength: rules?.min }));
            }
            if (rules.max) {
                schema = schema.max(rules?.max, i18n.t(`common::msg::This field can be up to {{maxLength}} characters.`, `This field can be up to {{maxLength}} characters.`, { maxLength: rules?.max }));
            }
            if (rules.pattern) {
                schema = schema.matches(rules?.pattern, { message: i18n.t(`common::msg::Invalid pattern.`, `Invalid pattern.`), excludeEmptyString: true });
            }
            // Range date picker는 "시작~종료" 한 문자열로 저장돼 pattern이 형식만 보장한다.
            // 역전된 기간이 그대로 저장되는 것을 막기 위해 순서까지 검사한다.
            if (rules?.pattern === RANGE_DATE_PATTERN) {
                schema = schema.test(
                    "range-date-order",
                    i18n.t(`common::msg::The start date must be on or before the end date.`, `The start date must be on or before the end date.`),
                    (value) => {
                        if (!value) return true;
                        const [start, end] = String(value).split("~");
                        // 한쪽만 입력했거나 형식이 어긋난 경우는 위 pattern 검사가 담당한다.
                        if (!start || !end) return true;
                        return start <= end; // YYYY-MM-DD 고정 폭이라 문자열 비교가 날짜 순서와 일치한다
                    },
                );
            }
        } else if (fieldType === "number") {
            schema = yup
                .number(i18n.t(`common::msg::This field should be a number.`, `This field should be a number.`))
                .transform((value, originalValue) => {
                    // 원래 값이 NaN인 경우 null로 변환
                    return originalValue === "" || isNaN(originalValue) ? null : value;
                })
                .nullable(true)
                .test(REQUIRED_TEST_NAME, i18n.t(`common::msg::This field is required.`, `This field is required.`), function (value) {
                    const { path, createError } = this;
                    // addRequired가 true이고 value가 null이 아니면 required 검사
                    if (addRequired && value === null) {
                        return createError({ path, message: i18n.t(`common::msg::This field is required.`, `This field is required.`) });
                    }
                    return true;
                });
            if (rules.min) {
                schema = schema.min(rules?.min, i18n.t(`common::msg::This field can be at least {{min}}.`, `This field can be at least {{min}}.`, { min: rules?.min }));
            }
            if (rules.max) {
                schema = schema.max(rules?.max, i18n.t(`common::msg::This field can be up to {{max}}.`, `This field can be up to {{max}}.`, { max: rules?.max }));
            }
        }
        if (fieldType === "object") {
            schema = yup.object().nullable(true);
            if (addRequired) {
                schema = schema.required(i18n.t(`common::msg::This field is required.`, `This field is required.`));
            }
        }
        if (fieldType === "enum") {
            //Note: enum의 경우 default value가 아닌 옵션의 값을 검증합니다.
            // 허용 옵션 목록 — parseEnumType을 거치면 [{ id, value }], 원본 스키마면 문자열 배열로 들어온다.
            // options를 넘기지 않는 호출부에서는 목록이 비어 멤버십 검사를 건너뛴다(기존 동작 유지).
            const allowedValues = (options ?? [])
                .map((option) => option?.value ?? option)
                .filter((v) => v !== null && v !== undefined);
            const notAllowedMessage = i18n.t(`common::msg::This value is not one of the allowed options.`, `This value is not one of the allowed options.`);
            const isAllowedValue = (v) => allowedValues.length === 0 || allowedValues.includes(v);

            const lengthRules = (base) =>
                base
                    .min(rules?.min ?? 0, i18n.t(`common::msg::This field can be from {{min}} characters.`, `This field can be from {{min}} characters.`, { min: rules?.min ?? 0 }))
                    .max(rules?.max ?? 255, i18n.t(`common::msg::This field can be up to {{maxLength}} characters.`, `This field can be up to {{maxLength}} characters.`, { maxLength: rules?.max ?? 255 }));

            if (type === "array") {
                // MULTI_SELECT — 값이 배열이므로 모든 항목이 허용 옵션에 있어야 한다.
                // 배열 항목은 null이 될 수 없어 기존 when 구성을 그대로 둔다.
                schema = yup
                    .array()
                    .nullable(true)
                    .of(
                        lengthRules(yup.string())
                            .when([], {
                                is: () => rules?.pattern,
                                then: yup.string().matches(rules?.pattern, i18n.t(`common::msg::Invalid pattern.`, `Invalid pattern.`)),
                            })
                            .when([], {
                                is: () => addRequired,
                                then: yup.string().required(),
                            }),
                    )
                    .unique(i18n.t(`common::msg::Duplicate value.`, `Duplicate value.`), (v) => v)
                    .test("enum-option-membership", notAllowedMessage, (value) => !Array.isArray(value) || value.every(isAllowedValue));
                if (addRequired) {
                    // nullable(true)로 바뀌면서 값이 없는 경우가 통과하게 되므로,
                    // string/object 브랜치와 같은 방식으로 배열 자체에 required를 건다.
                    // (기존에는 null이 "must be a `array` type" 타입 에러로 우연히 막혔다)
                    const requiredMessage = i18n.t(`common::msg::This field is required.`, `This field is required.`);
                    // yup 0.32의 array().required()는 null/undefined만 막고 빈 배열은 통과시킨다.
                    // MULTI_SELECT는 선택을 모두 해제하면 값이 []가 되므로 길이까지 봐야 한다.
                    // 같은 이름 + exclusive 테스트는 required가 등록한 테스트를 교체하므로,
                    // 빈 배열도 REQUIRED_TEST_NAME으로 잡혀 hasRequiredError가 "미입력"으로 식별한다
                    // (min으로 걸면 type이 "min"이라 길이 규칙 위반과 구분되지 않는다).
                    schema = schema.required(requiredMessage).test({
                        name: REQUIRED_TEST_NAME,
                        exclusive: true,
                        message: requiredMessage,
                        test: (value) => Array.isArray(value) && value.length > 0,
                    });
                }
            } else {
                // SINGLE_SELECT — 값이 스칼라 문자열이다. 배열 스키마를 쓰면 값이 있는 필드마다
                // "must be a `array` type" 타입 에러가 나 실제 규칙 위반을 가려버린다.
                // 위 string 브랜치와 같은 이유로 when을 쓰지 않는다 — then 쪽 yup.string()이
                // .nullable(true)를 덮어 null 값에서 typeError가 먼저 발생한다.
                schema = lengthRules(yup.string().nullable(true));
                if (rules?.pattern) {
                    schema = schema.matches(rules?.pattern, {
                        message: i18n.t(`common::msg::Invalid pattern.`, `Invalid pattern.`),
                        excludeEmptyString: true,
                    });
                }
                schema = schema.test("enum-option-membership", notAllowedMessage, (value) => checkIsNull(value) || isAllowedValue(value));
                if (addRequired) {
                    schema = schema.required(i18n.t(`common::msg::This field is required.`, `This field is required.`));
                }
            }
        }

        return yup.object().shape({ fieldValue: schema });
    };

    const checkIsNull = (el) => {
        return el === null || el === "" || el === undefined;
    };

    const getCustomMetadataByContentSchema = (customMetadata) => {
        return {
            id: customMetadata.id,
            name: customMetadata.name,
            description: customMetadata.description,
            fields: customMetadata?.fields
                ?.filter((el) => !el.fieldType?.dataType?.isReference)
                ?.map((el) => {
                    const fieldOptions = getCustomMetadataOptions(el);
                    const fieldValidationRule = createDefaultValueSchema(
                        el?.fieldType?.dataType?.type,
                        el?.fieldType?.dataType?.properties?.[0]?.type,
                        el?.fieldType?.dataType?.validation || el?.fieldType?.dataType?.properties?.[0]?.validation,
                        el?.isRequired && el?.isEditable,
                        fieldOptions,
                    );

                    return {
                        id: el.id,
                        subtitle: el.name,
                        description: el.description,
                        type: el.fieldType.dataType.detailType,
                        typeName: el.fieldType?.name,
                        value: el.defaultValue,
                        defaultValue: el.defaultValue,
                        options: fieldOptions,
                        isRequired: el.isRequired,
                        validationRule: fieldValidationRule,
                        // "필수 미입력" 여부. 규칙(스키마)을 단일 출처로 삼되 required 위반만 반영하므로
                        // 이 플래그의 의미는 "아직 안 채움"으로 유지된다(pattern/길이 위반은 여기서 제외).
                        // checkIsNull과 달리 MULTI_SELECT의 빈 배열([])도 미입력으로 잡는다.
                        validationError: hasRequiredError(fieldValidationRule, el.defaultValue),
                        isEditable: el.isEditable,
                        isHidden: el.isHidden,
                        placeholder: el.placeholder,
                    };
                }),
        };
    };

    const getFilteredResults = useCallback(
        ({ detailType }) => {
            const type = getResourceType(detailType);

            if (type === "ASSET") {
                return results?.filter((el) => el.state !== "DRAFT");
            }
            return results;
        },
        [results],
    );

    return {
        getFilteredResults,
        pending,
        viewMore,
        resetParams,
        getAssetType,
        getContentType,
        getResourceTitle,
        onResourceFetch,
        getResourceAssetData,
        getResourceTableColumns,
        getResourceSearchTableColumns,
        getResourceSmallTableColumns,
        getOptionsBySelectType,
        getCustomMetadataOptions,
        getCustomMetadataByContentSchema,
        checkIsNull,
        createDefaultValueSchema,
        getResourceType,
        getTableColumns,
        getSmallTableColumns,
        getSubNameIds,
    };
};

export default useCustomContent;
